/**
 * 风险评估卡片组件
 * 展示风险等级仪表盘和紧迫性文案
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, Shield } from "lucide-react";
import type {
  RiskAssessmentCardProps,
  SeverityLevel,
} from "@/types/conversion-funnel";
import { getSeverityColor, SEVERITY_COLORS } from "@/types/conversion-funnel";

/**
 * 严重程度配置
 */
const severityConfig: Record<
  SeverityLevel,
  {
    label: string;
    labelEn: string;
    icon: React.ReactNode;
    bgColor: string;
    textColor: string;
    gaugePercent: number;
  }
> = {
  high: {
    label: "高风险",
    labelEn: "High Risk",
    icon: <AlertTriangle className="h-5 w-5" />,
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    gaugePercent: 90,
  },
  medium: {
    label: "中风险",
    labelEn: "Medium Risk",
    icon: <Clock className="h-5 w-5" />,
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    gaugePercent: 60,
  },
  low: {
    label: "低风险",
    labelEn: "Low Risk",
    icon: <Shield className="h-5 w-5" />,
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    gaugePercent: 30,
  },
};

/**
 * 半圆形仪表盘组件
 */
interface GaugeProps {
  percent: number;
  color: string;
  size?: number;
}

function SemiCircleGauge({ percent, color, size = 120 }: GaugeProps) {
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <svg
      width={size}
      height={size / 2 + 10}
      viewBox={`0 0 ${size} ${size / 2 + 10}`}
      className="transform"
    >
      {/* 背景弧 */}
      <path
        d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${
          size - strokeWidth / 2
        } ${size / 2}`}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      {/* 进度弧 */}
      <path
        d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${
          size - strokeWidth / 2
        } ${size / 2}`}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        className="transition-all duration-500 ease-out"
      />
    </svg>
  );
}

/**
 * 风险评估卡片
 * 展示风险等级仪表盘、严重程度颜色和紧迫性文案
 */
export function RiskAssessmentCard({
  severity,
  className,
}: RiskAssessmentCardProps) {
  const { t } = useTranslation("assessments");
  const config = severityConfig[severity.level];
  const color = getSeverityColor(severity.level);

  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-4 shadow-sm",
        "flex flex-col h-full",
        config.bgColor,
        className
      )}
    >
      {/* 卡片标题 */}
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color }}>{config.icon}</span>
        <h3 className="text-sm font-semibold text-gray-700">
          {t("conversion.riskAssessment.title", "风险评估")}
        </h3>
      </div>

      {/* 仪表盘区域 */}
      <div className="flex flex-col items-center mb-3">
        <SemiCircleGauge percent={config.gaugePercent} color={color} />
        {/* 风险等级标签 */}
        <div
          className={cn(
            "mt-2 px-3 py-1 rounded-full text-sm font-semibold",
            severity.level === "high" && "bg-red-100 text-red-800",
            severity.level === "medium" && "bg-amber-100 text-amber-800",
            severity.level === "low" && "bg-green-100 text-green-800"
          )}
        >
          {config.label}
        </div>
      </div>

      {/* 紧迫性文案 */}
      <div className="flex-1">
        <p className={cn("text-sm leading-relaxed", config.textColor)}>
          {severity.urgency_text}
        </p>
      </div>

      {/* 颜色指示条 */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-xs text-gray-500">
            {t("conversion.riskAssessment.colorIndicator", "风险等级指示")}
          </span>
        </div>
      </div>
    </div>
  );
}

export default RiskAssessmentCard;
